import { useState } from 'react';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';

import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Dialog, Field, Input, Skeleton, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { ThongBaoHetHanMuc } from '../../../components/shared';
import { useT } from '../../../i18n';
import { tachLoiHanMuc } from '../../../lib/loi-han-muc';
import { loiTheoTruong } from '../../../lib/errors';
import { useToast } from '../../../lib/toast';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { BADGE_TOM_TAT } from '../constants';
import { useGhiPhien } from '../hooks/useBienBan';
import { dangXuLy, formTuTomTat, thanTomTat, viecThieuNoiDung } from '../nghiep-vu/bien-ban';
import type { FormTomTat } from '../nghiep-vu/bien-ban';
import type { Phien, TomTat } from '../types';

function HopSuaTomTat({ phienId, tomTat, onClose }: { phienId: string; tomTat: TomTat | null; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const { suaTomTat } = useGhiPhien();
  const [f, setF] = useState<FormTomTat>(() => formTuTomTat(tomTat));
  const [daThu, setDaThu] = useState(false);
  const loiServer = loiTheoTruong(suaTomTat.error);
  const thieu = viecThieuNoiDung(f);

  const doiViec = (i: number, khoa: 'task' | 'owner' | 'due', gt: string) =>
    setF((c) => ({ ...c, action_items: c.action_items.map((v, j) => (j === i ? { ...v, [khoa]: gt } : v)) }));

  const luu = () => {
    setDaThu(true);
    if (!f.overview.trim() || thieu.length > 0) return;
    suaTomTat.mutate({ id: phienId, vao: thanTomTat(f) }, {
      onSuccess: () => {
        toast.success(t('bienBanTomTat.daLuu'));
        onClose();
      },
    });
  };

  const loiChung = suaTomTat.error && !Object.keys(loiServer).some((k) => k === '/overview') ? dichLoi(suaTomTat.error) : null;

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('bienBanTomTat.hop.tieuDe')}
      width={880}
      mask={{ closable: false }}
      styles={{ body: { maxHeight: '72vh', overflowY: 'auto' } }}
      footer={
        <div className="flex flex-wrap justify-end gap-3">
          <Button variant="outline" size="md" onClick={onClose}>{t('chung.huy')}</Button>
          <Button variant="primary" size="md" loading={suaTomTat.isPending} onClick={luu}>{t('bienBanTomTat.hop.nut')}</Button>
        </div>
      }
    >
      <div className="space-y-4 pt-2 pr-1">
        <Field
          label={t('bienBanTomTat.hop.tongQuan')}
          required
          error={loiServer['/overview'] ?? (daThu && !f.overview.trim() ? t('bienBanTomTat.hop.loiTongQuan') : undefined)}
        >
          {(p) => <Textarea {...p} autoFocus autoSize={{ minRows: 3, maxRows: 8 }} value={f.overview} onChange={(e) => setF({ ...f, overview: e.target.value })} />}
        </Field>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label={t('bienBanTomTat.hop.yChinh')} hint={t('bienBanTomTat.hop.moiDong')} error={loiServer['/key_points']}>
            {(p) => <Textarea {...p} autoSize={{ minRows: 5, maxRows: 12 }} value={f.key_points} onChange={(e) => setF({ ...f, key_points: e.target.value })} />}
          </Field>
          <Field label={t('bienBanTomTat.hop.quyetDinh')} hint={t('bienBanTomTat.hop.moiDong')} error={loiServer['/decisions']}>
            {(p) => <Textarea {...p} autoSize={{ minRows: 5, maxRows: 12 }} value={f.decisions} onChange={(e) => setF({ ...f, decisions: e.target.value })} />}
          </Field>
        </div>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-content-primary">{t('bienBanTomTat.hop.viecCanLam')}</legend>
          {f.action_items.map((v, i) => (
            <div key={i} className="space-y-1">
              <div className="grid items-start gap-2 sm:grid-cols-[minmax(0,1fr)_10rem_8rem_auto]">
                <Input aria-label={t('bienBanTomTat.hop.noiDungViec', { so: i + 1 })} aria-invalid={daThu && thieu.includes(i)} status={daThu && thieu.includes(i) ? 'error' : undefined} placeholder={t('bienBanTomTat.hop.phViec')} value={v.task} onChange={(e) => doiViec(i, 'task', e.target.value)} />
                <Input aria-label={t('bienBanTomTat.hop.nguoiPhuTrach', { so: i + 1 })} placeholder={t('bienBanTomTat.hop.phNguoi')} value={v.owner} onChange={(e) => doiViec(i, 'owner', e.target.value)} />
                <Input aria-label={t('bienBanTomTat.hop.hanViec', { so: i + 1 })} placeholder={t('bienBanTomTat.hop.phHan')} value={v.due} onChange={(e) => doiViec(i, 'due', e.target.value)} />
                <Button
                  variant="ghost"
                  size="md"
                  aria-label={t('bienBanTomTat.hop.boViec', { so: i + 1 })}
                  title={t('bienBanTomTat.hop.boViec', { so: i + 1 })}
                  icon={<DeleteOutlined />}
                  onClick={() => setF((c) => ({ ...c, action_items: c.action_items.filter((_x, j) => j !== i) }))}
                />
              </div>
              {daThu && thieu.includes(i) && <p role="alert" className="text-xs text-danger-600">{t('bienBanTomTat.hop.loiViec')}</p>}
            </div>
          ))}
          <Button variant="outline" size="sm" icon={<PlusOutlined />} onClick={() => setF((c) => ({ ...c, action_items: [...c.action_items, { task: '', owner: '', due: '' }] }))}>
            {t('bienBanTomTat.hop.themViec')}
          </Button>
        </fieldset>
        {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}
      </div>
    </Dialog>
  );
}

function DanhSachY({ tieuDe, ds }: { tieuDe: string; ds: readonly string[] }) {
  const { t } = useT();
  return (
    <section>
      <h4 className="mb-1 text-xs font-semibold text-content-secondary">{tieuDe}</h4>
      {ds.length === 0 ? (
        <p className="text-xs text-content-muted">{t('bienBanTomTat.khongCoMuc')}</p>
      ) : (
        <ul className="list-disc space-y-1 pl-4 text-sm leading-relaxed text-content-primary">
          {ds.map((y, i) => <li key={i} className="break-words">{y}</li>)}
        </ul>
      )}
    </section>
  );
}

/**
 * Khối tóm tắt, trạng thái và lỗi riêng với phiên: tóm tắt lỗi thì bản chép lời vẫn dùng được (ADR-0126 mục 4).
 * Có bản đã sửa tay thì Tóm tắt lại phải hỏi trước, vì phần đã sửa mất hẳn.
 */
export function TomTatPhien({ phien, coSua }: { phien: Phien; coSua: boolean }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const { tomTatLai } = useGhiPhien();
  const [moSua, setMoSua] = useState(false);
  const [hoiLai, setHoiLai] = useState(false);
  const tt = phien.summary;
  const trang = phien.summary_status;
  const dangChay = trang === 'PENDING' || phien.status === 'SUMMARIZING';
  const chuaChepXong = phien.status === 'QUEUED' || phien.status === 'TRANSCRIBING';
  const duocChay = coSua && phien.status === 'DONE' && !dangChay;
  const hanMuc = trang === 'FAILED' ? tachLoiHanMuc(phien.summary_error) : null;

  const chay = () =>
    tomTatLai.mutate(phien.id, {
      onSuccess: () => {
        toast.success(t('bienBanTomTat.daGuiTomTatLai'));
        setHoiLai(false);
      },
    });

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <CardTitle>{t('bienBanTomTat.tieuDe')}</CardTitle>
          {trang !== 'DONE' && trang !== 'NONE' && !chuaChepXong && <Badge variant={BADGE_TOM_TAT[trang].variant}>{t(BADGE_TOM_TAT[trang].khoa)}</Badge>}
          {tt?.edited && <Badge variant="neutral">{t('bienBanTomTat.daSuaTay')}</Badge>}
        </div>
        {duocChay && (
          <div className="flex flex-wrap gap-1">
            {tt && (
              <Button variant="ghost" size="sm" icon={<EditOutlined />} onClick={() => setMoSua(true)}>{t('bienBanTomTat.suaTay')}</Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              icon={<ReloadOutlined />}
              loading={tomTatLai.isPending && !hoiLai}
              onClick={() => (tt?.edited ? setHoiLai(true) : chay())}
            >
              {tt || trang === 'FAILED' ? t('bienBanTomTat.tomTatLai') : t('bienBanTomTat.taoTomTat')}
            </Button>
          </div>
        )}
      </CardHeader>
      <CardBody className="space-y-4 py-4">
        {hanMuc && <ThongBaoHetHanMuc bienThe="gon" thongTin={hanMuc} nhanThuLai={duocChay ? t('bienBanTomTat.tomTatLai') : undefined} />}
        {trang === 'FAILED' && !hanMuc && (
          <div role="alert" className="flex items-start gap-2 rounded bg-warning-50 px-3 py-2.5">
            <WarningOutlined aria-hidden className="mt-0.5 text-warning-600" />
            <p className="min-w-0 text-sm text-warning-700">{phien.summary_error || t('bienBanTomTat.loiMacDinh')}</p>
          </div>
        )}
        {tomTatLai.error && !hoiLai && <LoiTrongHop>{dichLoi(tomTatLai.error)}</LoiTrongHop>}

        {dangChay && (
          <div aria-busy className="space-y-2.5">
            <p role="status" className="text-xs text-content-secondary">{t('bienBanTomTat.dangTomTat')}</p>
            {!tt && ['w-full', 'w-5/6', 'w-2/3'].map((r) => <Skeleton key={r} className={`h-4 ${r}`} />)}
          </div>
        )}
        {chuaChepXong && <p className="text-xs text-content-muted">{t('bienBanTomTat.choChepLoi')}</p>}
        {!tt && !dangChay && !chuaChepXong && trang !== 'FAILED' && dangXuLy(phien.status) === false && (
          <p className="text-xs text-content-muted">{t('bienBanTomTat.chuaCo')}</p>
        )}

        {tt && (
          <>
            <section>
              <h4 className="mb-1 text-xs font-semibold text-content-secondary">{t('bienBanTomTat.tongQuan')}</h4>
              <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-content-primary">{tt.overview}</p>
            </section>
            <DanhSachY tieuDe={t('bienBanTomTat.yChinh')} ds={tt.key_points} />
            <DanhSachY tieuDe={t('bienBanTomTat.quyetDinh')} ds={tt.decisions} />
            <section>
              <h4 className="mb-1 text-xs font-semibold text-content-secondary">{t('bienBanTomTat.viecCanLam')}</h4>
              {tt.action_items.length === 0 ? (
                <p className="text-xs text-content-muted">{t('bienBanTomTat.khongCoMuc')}</p>
              ) : (
                <ul className="divide-y divide-[var(--border-default)] rounded border border-default">
                  {tt.action_items.map((v, i) => (
                    <li key={i} className="px-3 py-2">
                      <p className="break-words text-sm text-content-primary">{v.task}</p>
                      {(v.owner || v.due) && (
                        <p className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-content-muted">
                          {v.owner && <span>{t('bienBanTomTat.phuTrach', { ten: v.owner })}</span>}
                          {v.due && <span>{t('bienBanTomTat.han', { han: v.due })}</span>}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </CardBody>
      {moSua && <HopSuaTomTat phienId={phien.id} tomTat={tt} onClose={() => setMoSua(false)} />}
      <HopXacNhanThaoTac
        open={hoiLai}
        tieuDe={t('bienBanTomTat.xacNhanLai.tieuDe')}
        than={t('bienBanTomTat.xacNhanLai.than')}
        nhanXacNhan={t('bienBanTomTat.xacNhanLai.nut')}
        nguyHiem
        dangChay={tomTatLai.isPending}
        loi={tomTatLai.error && hoiLai ? dichLoi(tomTatLai.error) : null}
        onClose={() => setHoiLai(false)}
        onXacNhan={chay}
      />
    </Card>
  );
}
